import { Link } from "react-router";
import styled from "styled-components";

import { curatedWallets, walletCategories } from "./curatedWalletData";
import { SANCTIONED_ADDRESS_COUNT } from "./sanctionsSummary";
import { walletLists } from "./walletLists";
import { truncateHash } from "./utils";

const Section = styled.section`
  margin-top: 2rem;
`;

const SectionHeader = styled.h3`
  margin-bottom: 0.2rem;
`;

const Disclaimer = styled.p`
  font-size: 0.8rem;
  color: #a3abba;
  margin: 0 0 1rem 0;
  max-width: 60ch;
`;

const CategoryName = styled.h4`
  margin: 1.2rem 0 0.4rem 0;
  border-bottom: 1px dotted #3f3f44;
`;

const WalletCard = styled(Link)`
  display: block;
  padding: 0.6rem 0.8rem;
  margin-bottom: 0.5rem;
  border: 1px solid #3f3f44;
  border-radius: 4px;
  text-decoration: none;

  &:hover {
    border-color: #32a1ce;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const WalletLabel = styled.span`
  font-weight: bold;
`;

const Holdings = styled.span`
  white-space: nowrap;
  color: #26a65b;
`;

const Note = styled.p`
  margin: 0.3rem 0;
  font-size: 0.85rem;
  max-width: 75ch;
`;

const Address = styled.code`
  font-size: 0.75rem;
  color: #a3abba;
  word-break: break-all;
`;

// Where the explorer's own lists live. One entry today; the shape is here so a
// second list is a row rather than a rewrite.
const OtherLists = styled.div`
  margin-top: 1.6rem;
  padding-top: 1rem;
  border-top: 1px dotted #3f3f44;
`;

const ListLink = styled(Link)`
  display: block;
  padding: 0.6rem 0.8rem;
  border: 1px solid #3f3f44;
  border-radius: 4px;
  text-decoration: none;
  max-width: 75ch;

  &:hover {
    border-color: #32a1ce;
  }
`;

const CuratedWalletList = () => (
  <Section>
    <SectionHeader>Notable wallets</SectionHeader>
    <Disclaimer>
      Labels are attributions from public explorer tags, reporting and, where a
      note says so, the owner's own published list — not proof of ownership.
      Holdings are approximate, as of September 2026.
    </Disclaimer>

    {walletCategories.map((category) => {
      const inCategory = curatedWallets.filter((w) => w.category === category);
      if (inCategory.length === 0) return null;

      return (
        <div key={category}>
          <CategoryName>{category}</CategoryName>
          {inCategory.map((wallet) => (
            <WalletCard
              key={wallet.address}
              to={`/btc-explorer/${wallet.address}`}
            >
              <CardTop>
                <WalletLabel>{wallet.label}</WalletLabel>
                <Holdings>~{wallet.approxBtc.toLocaleString()} BTC</Holdings>
              </CardTop>
              <Note>{wallet.note}</Note>
              <Address>{truncateHash(wallet.address)}</Address>
            </WalletCard>
          ))}
        </div>
      );
    })}

    <OtherLists>
      <CategoryName>Lists</CategoryName>
      {walletLists.map((list) => {
        const count = list.entries().length;
        return (
          <ListLink key={list.slug} to={`/btc-explorer/${list.slug}`}>
            <CardTop>
              <WalletLabel>{list.heading}</WalletLabel>
              <Holdings>
                {count} {count === 1 ? "address" : "addresses"}
              </Holdings>
            </CardTop>
            <Note>{list.intro}</Note>
          </ListLink>
        );
      })}
      <ListLink to="/btc-explorer/sanctioned">
        <CardTop>
          <WalletLabel>OFAC Sanctioned Addresses</WalletLabel>
          <Holdings>{SANCTIONED_ADDRESS_COUNT} addresses</Holdings>
        </CardTop>
        <Note>
          Every Bitcoin address on the US Treasury's Specially Designated
          Nationals list, grouped by the party it was designated against. Unlike
          the labels above, these are published government designations rather
          than community attributions.
        </Note>
      </ListLink>
    </OtherLists>
  </Section>
);

export default CuratedWalletList;
